import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, useWatch, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
 Heading,
 Button,
 Stack,
 HStack,
 Text,
 Center,
 Container,
 Grid,
 useToast,
 Flex,
 Box,
} from '@chakra-ui/react';
import {
 InputText,
 Select,
 CustomTextarea,
 CustomRadioGroup,
} from '../../components/Input';
import QuestionCardCustom from '../../components/Card/Question-card-custom';
import Options from './Options';
import GridOptions from '../../components/Grid/GridOptions';
import GridPreview from '../../components/Grid/GridPreview';
import { templateSchema } from '../../core/validations/survey_template';
import { QuestionnaireHeaderNew } from '../../components/Form';
import baseService from '../../services/base';
import { downloadExemploPerguntas } from '../../services/exemploService';
import { SuccessModal } from '../../components/Modals';
import {
 alternativesTotalOptions,
 defaultFields,
 formatQuestionObject,
 questionTypeOptions,
} from './resources';
import { handleFileChange } from './file-handler';
import { useCatalogoDeClassificacao } from '../../hooks/useCatalogoDeClassificacao';
import ClassificacaoQuestaoField from '../../components/Catalog/ClassificacaoQuestaoField';
import QuestionCardCertification from '../../components/Card/Question-card-certification';

export default function Template() {
 const toast = useToast();
 const inputFile = useRef(null);
 const navigate = useNavigate();

 const [isSubmitting, setIsSubmitting] = useState(false);
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [templateObjects, setTemplateObjects] = useState([]);
 const [optionsTotal, setOptionsTotal] = useState(0);
 const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
 const [isGridInitialized, setIsGridInitialized] = useState(false);

 const methods = useForm({
  defaultValues: {
   form: defaultFields,
  },
  resolver: yupResolver(templateSchema),
  mode: 'onChange',
 });

 const {
  handleSubmit,
  register,
  control,
  setValue,
  formState: { errors, isValid },
 } = methods;

 const { fields, append, remove } = useFieldArray({
  control,
  name: 'form',
 });

 const templateName = useWatch({
  control,
  name: 'name',
 });

 const templateCode = useWatch({
  control,
  name: 'templateCode',
 });

 const questionType = useWatch({
  control,
  name: `form.${fields?.length - 1}.type`,
 });

 const certificationPrerequisite = useWatch({
  control,
  name: `form.${fields?.length - 1}.isCertified`,
 });

 const hasHelper =
  useWatch({
   control,
   name: `form.${fields?.length - 1}.hasHelper`,
  }) || 'Não';

 const watchedGridRows = useWatch({
  control,
  name: `form.${fields?.length - 1}.gridRows`,
  defaultValue: [],
 });

 const watchedGridColumns = useWatch({
  control,
  name: `form.${fields?.length - 1}.gridColumns`,
  defaultValue: [],
 });

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const removeTemplateObject = (index) => {
  const updatedTemplateObjects = [...templateObjects];
  updatedTemplateObjects.splice(index, 1);
  setTemplateObjects(updatedTemplateObjects);
 };

 const catalogoDeClassificacao = useCatalogoDeClassificacao();

 const onChangeFile = (event) => {
  const file = event.target.files[0];
  handleFileChange(file, setTemplateObjects, showErrorToast, catalogoDeClassificacao);
 };

 const addQuestion = () => {
  setOptionsTotal(0);
  append({
   code: '',
   title: '',
   type: '',
   // EG-41: a questão nasce sem classificação — é opcional pelo CA03.
   id_pillar: null,
   id_theme: null,
   previousYears: 'Não',
   isRequired: 'Não',
   evidenceOnFile: 'Não',
   evidenceInImages: 'Não',
   hasAverage: 'Não',
   hasSum: 'Não',
   hasHelper: 'Não',
   gridRows: [],
   gridColumns: [],
  });
 };

 const onSubmit = (data) => {
  alert(JSON.stringify(data));
 };

 const closeSuccessModal = () => {
  setIsSubmitSuccessful(false);
  navigate('/templates');
 };

 const {
  fields: options,
  append: appendOptions,
  remove: removeOptions,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.options`,
 });

 const {
  fields: gridRows,
  append: appendGridRow,
  remove: removeGridRow,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.gridRows`,
 });

 const {
  fields: gridColumns,
  append: appendGridColumn,
  remove: removeGridColumn,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.gridColumns`,
 });

 const handlenumberOfAlternativesChange = (e) => {
  const count = parseInt(e.target.value);
  if (count > optionsTotal) {
   for (let i = optionsTotal; i < count; i++) {
    appendOptions(
     {
      description: '',
     },
     {
      shouldFocus: false,
     }
    );
   }
  } else if (count < optionsTotal) {
   for (let i = optionsTotal - 1; i >= count; i--) {
    removeOptions(i);
   }
  }
  setOptionsTotal(count);
 };

 const handleAddGridRow = (questionIndex) => {
  appendGridRow({
   description: '',
   row_order: gridRows.length,
  });
 };

 const handleRemoveGridRow = (questionIndex, rowIndex) => {
  removeGridRow(rowIndex);
 };

 const handleAddGridColumn = (questionIndex) => {
  appendGridColumn({
   description: '',
  });
 };

 const handleRemoveGridColumn = (questionIndex, colIndex) => {
  removeGridColumn(colIndex);
 };

 const submitFirst = async () => {
  try {
   setIsSubmitting(true);

   const allQuestions = fields.slice(0, -1).map(formatQuestionObject);

   if (templateObjects.length > 0) {
    const formattedTemplateObjects = templateObjects?.map(formatQuestionObject);
    allQuestions.push(...formattedTemplateObjects);
   }
   const form = {
    template: {
     name: templateName,
     code: templateCode,
    },
    Questions: allQuestions,
   };
    await baseService.post('survey-template/create-template-with-questions', {
     ...form,
    });

   setIsSubmitSuccessful(true);
  } catch (err) {
   console.log('err', err);
   showErrorToast('Não foi possível salvar o template');
  } finally {
   setIsSubmitting(false);
  }
 };

 useEffect(() => {
  if (questionType === 'multiple_choice') {
   setIsCheckboxSelected(true);
  } else {
   setIsCheckboxSelected(false);
  }

  // Inicializar grid com valores padrão quando tipo grid é selecionado
  if (questionType === 'grid' && !isGridInitialized && gridRows.length === 0) {
   setIsGridInitialized(true);
   // Adicionar 3 linhas padrão
   appendGridRow({ description: '', row_order: 0 });
   appendGridRow({ description: '', row_order: 1 });
   appendGridRow({ description: '', row_order: 2 });
   
   // Adicionar 4 colunas padrão
   appendGridColumn({ description: '' });
   appendGridColumn({ description: '' });
   appendGridColumn({ description: '' });
   appendGridColumn({ description: '' });
  } else if (questionType !== 'grid') {
   setIsGridInitialized(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [questionType]);

 useEffect(() => {
  if (
   (questionType === 'text' ||
    questionType === 'percentage' ||
    questionType === 'number' ||
    questionType === 'money' ||
    questionType === 'grid') &&
   options?.length
  ) {
   setOptionsTotal(0);
   removeOptions();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [questionType, options?.length]);

 return (
  <FormProvider {...methods}>
   <form onSubmit={handleSubmit(onSubmit)}>
    <QuestionnaireHeaderNew
     heading='Cadastrar Template'
     submitFunction={submitFirst}
     isSubmitting={isSubmitting}
     isSubmitEnabled={
      (fields?.length > 1 || templateObjects?.length) && templateName && templateCode
     }
     isFullWidth
    >
     <Flex align='baseline' justify='flex-start' gap='4'>
      <Flex w='180px'>
       <InputText
        {...register('templateCode')}
        error={errors.templateCode}
        placeholder='Código do template'
        w='180px'
        autoComplete='off'
       />
      </Flex>
      <InputText
       {...register('name')}
       error={errors.name}
       placeholder='Digite o nome do template'
       w='500px'
       autoComplete='off'
      />
     </Flex>
    </QuestionnaireHeaderNew>

    <Center my={['8px', '24px']} px={{ xl: '86px' }}>
     <Container maxW={'1700px'} p={0} minH='80vh'>
      <HStack
       bg='white'
       justify='space-between'
       p='24px'
       borderRadius='12px 12px 0 0'
       borderBottom='2px solid #F5F5FB'
      >
       <Heading as='h3' fontSize='20px' fontWeight={600}>
        Adicionando perguntas
       </Heading>

       <HStack h='49px' spacing='24px'>
        <Button
         onClick={downloadExemploPerguntas}
         variant='outline'
         bg='none'
         color='green.100'
         w='240px'
         h='full'
        >
         Baixar planilha em lote
        </Button>
        <Button
         onClick={() => {
          inputFile.current.click();
         }}
         variant='primary'
         bg='green.100'
         w='199px'
         h='full'
        >
         Adicionar em lote
        </Button>
       </HStack>
      </HStack>

      {fields?.map(
       (field, index) =>
        index === fields?.length - 1 && (
         <Stack
          key={field.id}
          bg='white'
          spacing='26px'
          p={6}
          borderRadius='0 0 12px 12px'
         >
          <Grid
           templateColumns={
            isCheckboxSelected ? '171px 5fr 3fr 1fr 145px' : '171px 6fr 3fr 145px'
           }
           gap={6}
          >
           <InputText
            {...register(`form.${index}.code`)}
            label='Código da pergunta'
            placeholder='Código da pergunta'
            labelSize='sm'
            error={errors['form']?.[index]?.code}
           />

           <InputText
            {...register(`form.${index}.title`)}
            label='Título da pergunta'
            placeholder='Digite o título da pergunta'
            labelSize='sm'
            error={errors['form']?.[index]?.title}
            expandable
           />

           <Select
            {...register(`form.${index}.type`)}
            placeholder='Selecione o tipo de pergunta'
            label='Tipo de pergunta'
            error={errors['form']?.[index]?.type}
            minW='300px'
            options={questionTypeOptions}
           />

           {isCheckboxSelected && (
            <CustomRadioGroup
             register={register}
             label='Com pontuação?'
             fieldName={`form.${index}.isCertified`}
            />
           )}

           <Select
            name={`numberOfAlternatives_${index}`}
            value={optionsTotal.toString()}
            onChange={handlenumberOfAlternativesChange}
            label='Núm. de alternativas'
            placeholder='Selecione'
            isDisabled={
             questionType === 'text' ||
             questionType === '' ||
             questionType === 'percentage' ||
             questionType === 'number' ||
             questionType === 'money' ||
             questionType === 'grid'
            }
            options={alternativesTotalOptions}
           />
          </Grid>

          {/* EG-41 — CA01/CA02/CA03: a classificação da pergunta em seção
              própria, FORA do Grid de colunas fixas acima. Dentro dele os
              seletores estouravam a grade e vazavam para fora da caixa. */}
          <ClassificacaoQuestaoField
           control={control}
           setValue={setValue}
           prefixo={`form.${index}`}
           erro={errors['form']?.[index]?.id_theme?.message}
          />

          {/* Opções para multi_choice e multiple_choice */}
          {(questionType === 'multi_choice' || questionType === 'multiple_choice') &&
          optionsTotal !== 0 ? (
           <Options
            index={index}
            register={register}
            errors={errors}
            options={options}
            isCheckboxSelected={isCheckboxSelected}
            certificationPrerequisite={certificationPrerequisite}
           />
          ) : null}

          {/* Configuração de Grid */}
          {questionType === 'grid' && (
           <>
            <GridOptions
             index={index}
             register={register}
             errors={errors}
             gridRows={gridRows}
             gridColumns={gridColumns}
             onAddRow={handleAddGridRow}
             onRemoveRow={handleRemoveGridRow}
             onAddColumn={handleAddGridColumn}
             onRemoveColumn={handleRemoveGridColumn}
            />
            <Box>
             <Text fontWeight={600} fontSize='16px' mb='4'>
              Pré-visualização
             </Text>
             <GridPreview 
              gridRows={watchedGridRows || gridRows} 
              gridColumns={watchedGridColumns || gridColumns} 
             />
            </Box>
           </>
          )}

          <HStack spacing={6} justify='flex-start' flexWrap='wrap'>
           <CustomRadioGroup
            register={register}
            label='Pergunta de anos anteriores?'
            fieldName={`form.${index}.previousYears`}
           />
           <CustomRadioGroup
            register={register}
            label='Pergunta obrigatória?'
            fieldName={`form.${index}.isRequired`}
           />
           <CustomRadioGroup
            register={register}
            label='Evidências em arquivos?'
            fieldName={`form.${index}.requestFiles`}
           />

           <CustomRadioGroup
            register={register}
            label='Evidências em fotos/imagens?'
            fieldName={`form.${index}.requestImages`}
           />
           <CustomRadioGroup
            register={register}
            label='Possui campo de ajuda/helper?'
            fieldName={`form.${index}.hasHelper`}
           />
           <CustomRadioGroup
            register={register}
            label='Possui SOMA do período?'
            fieldName={`form.${index}.hasSum`}
            hasTooltip
            tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma SOMA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
            isDisabled={
             questionType === 'text' ||
             questionType === 'multi_choice' ||
             questionType === 'multiple_choice'
            }
           />
           <CustomRadioGroup
            register={register}
            label='Possui MÉDIA do período?'
            fieldName={`form.${index}.hasAverage`}
            hasTooltip
            tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma MÉDIA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
            isDisabled={
             questionType === 'text' ||
             questionType === 'multi_choice' ||
             questionType === 'multiple_choice'
            }
           />
          </HStack>

          <Stack spacing={6}>
           <CustomTextarea
            {...register(`form.${index}.helper.description`)}
            label='Texto de ajuda/helper'
            placeholder='Se necessário forneça uma descrição para a pergunta'
            h='111px'
            isDisabled={hasHelper === 'Não'}
           />
           <InputText
            {...register(`form.${index}.helper.url_video`)}
            label='URL de ajuda/helper'
            placeholder='Informe a URL do video'
            labelSize='sm'
            isDisabled={hasHelper === 'Não'}
           />
          </Stack>
          <Button
           variant='primary'
           w='212px'
           h='49px'
           ml='auto'
           isDisabled={!isValid}
           onClick={addQuestion}
          >
           Adicionar pergunta
          </Button>
         </Stack>
        )
      )}

      <Stack spacing={0} mt='32px'>
       <Text fontWeight={600} mb='24px'>Perguntas adicionadas</Text>

       {fields.length === 1 && !templateObjects?.length && (
        <Center h='143px' bg='rgba(173, 175, 179, 0.1)' borderRadius='12px' mt='6'>
         <Text fontWeight={700} color='#5C6066'>
          Nenhuma pergunta foi adicionada
         </Text>
        </Center>
       )}

       {fields?.length > 1 || templateObjects?.length > 1 ? (
        <Stack spacing='16px'>
         {fields?.map(
          (field, i) =>
           i !== fields?.length - 1 && (
            <QuestionCardCertification
             key={i}
             index={i}
             remove={() => remove(i)}
             question={{
              ...field,
              description: field.title,
              isCertified: field?.isCertified === 'Sim',
             }}
            />
           )
         )}

         {templateObjects?.map((field, i) => (
          <QuestionCardCertification
           key={i}
           index={i}
           remove={() => removeTemplateObject(i)}
           question={{
            ...field,
            description: field.title
           }}
          />
         ))}
        </Stack>
       ) : null}
      </Stack>

      <input
       type='file'
       id='file'
       ref={inputFile}
       style={{ display: 'none' }}
       onChange={(e) => onChangeFile(e)}
      />
     </Container>
    </Center>

    <SuccessModal
     isOpen={isSubmitSuccessful}
     onClose={closeSuccessModal}
     title='Template criado com sucesso!'
    />
   </form>
  </FormProvider>
 );
}
